import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";

export interface CrmActionEffectivenessFilters {
  actionRef?: string;
  contractRef?: string;
  sourceEventType?: string;
  signalFamily?: string;
}

export interface CrmActionEffectivenessBucket {
  reviewed: number;
  accepted: number;
  dismissed: number;
  materialized: number;
  crmMaterialized: number;
  manualResolved: number;
  acceptanceRate: number;
  materializationRate: number;
  crmMaterializationRate: number;
  manualResolutionRate: number;
  avgTimeToMaterializeMinutes: number | null;
}

export interface CrmActionEffectivenessNamedBucket extends CrmActionEffectivenessBucket {
  actionRef?: string;
  intent?: string;
  sourceSurface?: string;
  sourceEventType?: string;
  signalFamily?: string;
}

export interface CrmActionMaterialization {
  id: string;
  decisionEventId: string;
  contactId: string | null;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
  recommendationId: string;
  actionRef: string;
  intent: string;
  materializedEntityType: "task" | "person" | "deal" | "activity" | "company" | null;
  materializedEntityId: string | null;
  materializationStatus: "created" | "updated" | "existing" | null;
  manualResolution: Record<string, unknown> | null;
  sourceEventType?: string | null;
  signalFamily?: string | null;
  sourceSurface: string;
  acceptedAt: string;
  materializedAt: string;
  minutesToMaterialize: number;
}

export interface CrmActionEffectiveness {
  windowDays: number;
  filters: {
    actionRef: string | null;
    contractRef: string | null;
    sourceEventType: string | null;
    signalFamily: string | null;
  };
  totals: CrmActionEffectivenessBucket;
  byActionRef: CrmActionEffectivenessNamedBucket[];
  byIntent: CrmActionEffectivenessNamedBucket[];
  bySourceSurface: CrmActionEffectivenessNamedBucket[];
  bySourceEventType: CrmActionEffectivenessNamedBucket[];
  bySignalFamily: CrmActionEffectivenessNamedBucket[];
  recentMaterializations: CrmActionMaterialization[];
}

export function useCrmActionEffectiveness(filters: CrmActionEffectivenessFilters = {}) {
  const [data, setData] = useState<CrmActionEffectiveness | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEffectiveness = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      const actionRef = filters.actionRef ?? filters.contractRef;
      if (actionRef) {
        params.set("actionRef", actionRef);
      }
      if (filters.sourceEventType) {
        params.set("sourceEventType", filters.sourceEventType);
      }
      if (filters.signalFamily) {
        params.set("signalFamily", filters.signalFamily);
      }
      const query = params.toString();
      const response = await apiFetch<{ data: CrmActionEffectiveness }>(
        `/crm/intelligence/action-recommendations/effectiveness${query ? `?${query}` : ""}`,
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load action effectiveness");
    } finally {
      setIsLoading(false);
    }
  }, [filters.actionRef, filters.contractRef, filters.signalFamily, filters.sourceEventType]);

  useEffect(() => {
    fetchEffectiveness();
  }, [fetchEffectiveness]);

  return { data, isLoading, error, refetch: fetchEffectiveness };
}
